<template>
  <div class="word-card">
    <!-- Mobile layout -->
    <div class="mobile-layout">
      <div class="word-content">
        <span class="word-text" :class="sourceWordClass">{{ word }}</span>
        <span
            v-if="displayTransliteration"
            class="word-transliteration"
            :class="translitWordClass">
          ({{ transliteration }})
        </span>
        <span class="word-separator">—</span>
        <span class="word-translation" :class="translationWordClass">{{ translation }}</span>
      </div>
      <div v-if="mnemonicImageUrl || mnemonicText" class="word-mnemonic">
        <img
            v-if="mnemonicImageUrl"
            :src="mnemonicImageUrl"
            :alt="word"
            class="word-mnemonic-image"
        >
        <div class="word-mnemonic-body">
          <div v-if="showNormalized" class="word-normalized">
            <span class="word-normalized-bracket"></span>
            <span :class="sourceWordClass">{{ normalizedWord }}</span>
            <span class="word-separator">—</span>
            <span :class="translationWordClass">{{ normalizedTranslation }}</span>
            <span class="word-normalized-bracket"></span>
          </div>
          <div v-if="mnemonicText" class="word-mnemonic-label">{{ t('wordCard.mnemonicLabel') }}</div>
          <div
              v-if="mnemonicText"
              class="word-mnemonic-text"
              v-html="mnemonicText"
          ></div>
        </div>
      </div>
      <div v-if="showNormalized && !mnemonicImageUrl && !mnemonicText" class="word-normalized">
        <span class="word-normalized-bracket">(</span>
        <span :class="sourceWordClass">{{ normalizedWord }}</span>
        <span class="word-separator">—</span>
        <span :class="translationWordClass">{{ normalizedTranslation }}</span>
        <span class="word-normalized-bracket">)</span>
      </div>
    </div>

    <!-- Desktop layout: image left, title + description right -->
    <div v-if="mnemonicImageUrl || mnemonicText" class="desktop-layout">
      <img
          v-if="mnemonicImageUrl"
          :src="mnemonicImageUrl"
          :alt="word"
          class="word-mnemonic-image"
      >
      <div class="desktop-right">
        <div class="word-content">
          <span class="word-text" :class="sourceWordClass">{{ word }}</span>
          <span
              v-if="displayTransliteration"
              class="word-transliteration"
              :class="translitWordClass">
            ({{ transliteration }})
          </span>
          <span class="word-separator">—</span>
          <span class="word-translation" :class="translationWordClass">{{ translation }}</span>
        </div>
        <div v-if="showNormalized" class="word-normalized">
          <span class="word-normalized-bracket">(</span>
          <span :class="sourceWordClass">{{ normalizedWord }}</span>
          <span class="word-separator">—</span>
          <span :class="translationWordClass">{{ normalizedTranslation }}</span>
          <span class="word-normalized-bracket">)</span>
        </div>
        <div v-if="mnemonicText" class="word-mnemonic-label">{{ t('wordCard.mnemonicLabel') }}</div>
        <div
            v-if="mnemonicText"
            class="word-mnemonic-text"
            v-html="mnemonicText"
        ></div>
      </div>
    </div>

    <!-- Desktop layout without mnemonic: just the title -->
    <div v-if="!mnemonicImageUrl && !mnemonicText" class="desktop-layout desktop-layout--no-mnemonic">
      <div class="word-content">
        <span class="word-text" :class="sourceWordClass">{{ word }}</span>
        <span
            v-if="displayTransliteration"
            class="word-transliteration"
            :class="translitWordClass">
          ({{ transliteration }})
        </span>
        <span class="word-separator">—</span>
        <span class="word-translation" :class="translationWordClass">{{ translation }}</span>
      </div>
      <div v-if="showNormalized" class="word-normalized">
        <span class="word-normalized-bracket">(</span>
        <span :class="sourceWordClass">{{ normalizedWord }}</span>
        <span class="word-separator">—</span>
        <span :class="translationWordClass">{{ normalizedTranslation }}</span>
        <span class="word-normalized-bracket">)</span>
      </div>
    </div>

    <div class="word-meta">
      <slot name="meta-start"></slot>
      <div v-if="showLikeButtons && (mnemonicImageUrl || mnemonicText)" class="mnemonic-like-buttons">
        <button
            type="button"
            class="word-like-button"
            :class="{ active: mnemonicRating === 1 }"
            :disabled="mnemonicLikeDisabled || mnemonicLikePending"
            :aria-pressed="mnemonicRating === 1"
            @click="handleMnemonicLike(props.mnemonicRating === 1 ? 0 : 1)"
        >
          <svg class="word-like-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M2 20h2V8H2v12zm20-11a2 2 0 0 0-2-2h-6.31l.95-4.57.03-.32a1.5 1.5 0 0 0-.44-1.06L13.17 0 6.59 6.59A2 2 0 0 0 6 8v10a2 2 0 0 0 2 2h9a2.01 2.01 0 0 0 1.84-1.21l3.02-7.05A2 2 0 0 0 22 11V9z"/></svg>
        </button>
        <button
            type="button"
            class="word-like-button word-dislike-button"
            :class="{ active: mnemonicRating === -1 }"
            :disabled="mnemonicLikeDisabled || mnemonicLikePending"
            :aria-pressed="mnemonicRating === -1"
            @click="handleMnemonicLike(props.mnemonicRating === -1 ? 0 : -1)"
        >
          <svg class="word-like-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M22 4h-2v12h2V4zM2 15a2 2 0 0 0 2 2h6.31l-.95 4.57-.03.32a1.5 1.5 0 0 0 .44 1.06L10.83 24l6.58-6.59A2 2 0 0 0 18 16V6a2 2 0 0 0-2-2H7a2.01 2.01 0 0 0-1.84 1.21l-3.02 7.05A2 2 0 0 0 2 13v2z"/></svg>
        </button>
      </div>
      <button
          type="button"
          class="word-bookmark-button"
          :class="{ active: saved }"
          :aria-pressed="saved"
          @click="emit('toggle-saved')"
      >
        <svg class="word-bookmark-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 3h12v18l-6-4-6 4z" />
        </svg>
      </button>
      <slot name="meta-end"></slot>
    </div>
    <Transition name="like-toast">
      <div v-if="likeNotification" :key="likeNotification" class="like-notification" :class="likeNotification">
        {{ likeNotification === 'like' ? t('wordCard.likeNotification') : t('wordCard.dislikeNotification') }}
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, Transition } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  readonly word: string;
  readonly translation: string;
  readonly transliteration?: string;
  readonly normalizedWord?: string;
  readonly normalizedTranslation?: string;
  readonly sourceWordClass?: string | string[];
  readonly translationWordClass?: string | string[];
  readonly translitWordClass?: string | string[];
  readonly mnemonicImageUrl?: string;
  readonly mnemonicText?: string;
  readonly saved: boolean;
  readonly saving: boolean;
  readonly showLikeButtons?: boolean;
  readonly mnemonicRating?: number;
  readonly mnemonicLikePending?: boolean;
  readonly mnemonicLikeDisabled?: boolean;
}

const props = defineProps<Props>();
const { t } = useI18n();

const emit = defineEmits<{
  (e: 'toggle-saved'): void;
  (e: 'mnemonic-like', rating: number): void;
}>();

const likeNotification = ref<'like' | 'dislike' | null>(null);
let notificationTimer: ReturnType<typeof setTimeout> | null = null;

const handleMnemonicLike = (rating: number) => {
  emit('mnemonic-like', rating);
  if (rating === 1 || rating === -1) {
    likeNotification.value = rating === 1 ? 'like' : 'dislike';
    if (notificationTimer) clearTimeout(notificationTimer);
    notificationTimer = setTimeout(() => { likeNotification.value = null; }, 3000);
  } else {
    likeNotification.value = null;
    if (notificationTimer) clearTimeout(notificationTimer);
  }
};

const displayTransliteration = computed(() =>
    props.transliteration
    && props.transliteration !== props.word
    && props.transliteration !== props.translation
);

const normalize = (s: string) => s.normalize('NFC').trim().toLowerCase();

const showNormalized = computed(() => {
  if (!props.normalizedWord || !props.normalizedTranslation) return false;
  const wordDiffers = normalize(props.normalizedWord) !== normalize(props.word);
  const translationDiffers = normalize(props.normalizedTranslation) !== normalize(props.translation);
  return wordDiffers || translationDiffers;
});
</script>

<style scoped lang="less">
.word-card {
  position: relative;
  background: #ffffff;
  border: 1px solid #5c5f62;
  border-radius: 8px;
  padding: 16px 16px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  transition: transform 0.2s, box-shadow 0.2s;
}

.word-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.1);
}

.word-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.word-text {
  font-size: var(--font-size-regular);
  color: #1a1a1a;
}

.word-separator {
  color: #cccccc;
}

.word-translation {
  font-size: var(--font-size-regular);
  color: #666666;
}

.word-transliteration {
  font-size: var(--font-size-regular);
  color: #999999;
  font-style: italic;
}

.word-normalized {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  margin-bottom: 10px;
  color: #888;
}

.word-normalized-bracket {
  color: #888;
  display: none;
}

.word-mnemonic-body {
  min-width: 0;
  flex: 1;
}

.word-mnemonic-image {
  width: 166px;
  height: 166px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
  border: 1px solid #e0e0e0;
}

.word-mnemonic-label {
  font-size: 11px;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 20px;
  margin-bottom: 2px;
}

.word-mnemonic-text {
  margin-top: 0;
  font-size: var(--font-size-regular);
  line-height: 1.4;
  color: #555;
  flex: 1;
  min-width: 0;
  white-space: normal;

  :deep(strong),:deep(b) {
    color: #00986f;
    font-weight: normal;
  }
}

.word-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.word-bookmark-button {
  width: 42px;
  height: 42px;
  border: 1px solid #d7dce3;
  border-radius: 10px;
  background: #fff;
  color: #6a7686;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;
}

.word-bookmark-button.active {
  border-color: #8b97a8;
  color: #2f80ed;
  background: #f5f9ff;
}

.word-bookmark-button:disabled {
  opacity: 0.6;
  cursor: wait;
}

.word-bookmark-icon {
  width: 23px;
  height: 23px;
  fill: currentColor;
}

.mnemonic-like-buttons {
  display: flex;
  gap: 6px;
  margin-right: auto;
}

.word-like-button {
  width: 42px;
  height: 42px;
  border: 1px solid #d7dce3;
  border-radius: 10px;
  background: #fff;
  color: #6a7686;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;
}

.word-like-button.active {
  border-color: #8b97a8;
  color: #2f80ed;
  background: #f5f9ff;
}

.word-dislike-button.active {
  border-color: #e8a0a0;
  color: #d94f4f;
  background: #fef5f5;
}

.word-like-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.word-like-icon {
  width: 23px;
  height: 23px;
  fill: currentColor;
}

/* Desktop: show desktop-layout, hide mobile-layout */
.mobile-layout {
  display: none;
}

.desktop-layout {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.desktop-right {
  flex: 1;
  min-width: 0;
}

.desktop-layout .word-mnemonic-text {
  line-height: 1.4;
}

/* Mobile: show mobile-layout, hide desktop-layout */
@media (max-width: 800px) {
  .mobile-layout {
    display: block;
  }

  .desktop-layout {
    display: none;
  }

  .word-card {
    padding: 10px 16px;
    border-radius: 10px;
  }

  .word-content {
    gap: 6px;
  }

  .word-text {
    font-size: var(--font-size-regular);
  }

  .word-translation {
    font-size: var(--font-size-regular);
  }

  .word-transliteration {
    font-size: var(--font-size-regular);
  }

  .word-mnemonic {
    display: grid;
    grid-template-columns: 120px minmax(0, 1fr);
    gap: 12px;
    column-gap: 16px;
    margin: 10px 0;
    padding-top: 10px;
    border-top: 1px solid #e6edf5;
  }

  .word-mnemonic-image {
    width: 120px;
    height: 120px;
    border-radius: 7px;
  }

  .word-mnemonic-text {
    font-size: var(--font-size-regular);
    line-height: 1.35;
  }

  .word-bookmark-button {
    width: 39px;
    height: 39px;
    border-radius: 9px;
  }

  .word-like-button {
    width: 39px;
    height: 39px;
    border-radius: 9px;
  }

  .word-bookmark-icon,
  .word-like-icon {
    width: 21px;
    height: 21px;
  }
}

.like-notification {
  position: fixed;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  width: 70%;
  max-width: 400px;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 14px;
  text-align: center;
  pointer-events: none;
  z-index: 9999;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);

  &.dislike {
    background: #fef9e7;
    border: 1px solid #f0dca0;
    color: #7a6a2a;
  }

  &.like {
    background: #edf7ee;
    border: 1px solid #b8e0c0;
    color: #2a6a3a;
  }
}

.like-toast-enter-active,
.like-toast-leave-active {
  transition: opacity 0.3s ease;
}

.like-toast-enter-from,
.like-toast-leave-to {
  opacity: 0;
}
</style>
